import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { FC, FormEvent, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  checkLeadsAdminAccess,
  createLeadCampaign,
  createLeadGroup,
  deleteLead,
  deleteLeadGroup,
  emailLead,
  fetchLeadBrandDefaults,
  fetchLeadCampaign,
  fetchLeadCampaigns,
  fetchLeadEmailReplies,
  fetchLeadGroups,
  fetchLeads,
  fetchLeadStats,
  previewLeadCampaignAudience,
  resendLeadCampaign,
  saveLeadBrandDefaults,
  sendLeadCampaign,
  updateLead,
  updateLeadCampaign,
  updateLeadGroupMembers,
} from '../../api/leads';
import { LeadCampaignPanel } from '../../components/leads/LeadCampaignPanel';
import { LeadConfirmModal } from '../../components/leads/LeadConfirmModal';
import { LeadCreateModal } from '../../components/leads/LeadCreateModal';
import { LeadEmailBrandPanel } from '../../components/leads/LeadEmailBrandPanel';
import { isLeadEmailBodyEmpty, LeadEmailBodyEditor } from '../../components/leads/LeadEmailBodyEditor';
import { LeadEmailRepliesPanel } from '../../components/leads/LeadEmailRepliesPanel';
import { LeadImportModal } from '../../components/leads/LeadImportModal';
import { LeadMergeTagSubjectInput } from '../../components/leads/LeadMergeTagText';
import { LeadNewsletterComposer } from '../../components/leads/LeadNewsletterComposer';
import { LeadNewsletterSendModal } from '../../components/leads/LeadNewsletterSendModal';
import { LeadFlagBadge, LeadStageBadge, LeadStatusBadge } from '../../components/leads/LeadStageBadge';
import { useAuthState } from '../../contexts/authcontext';
import {
  LeadCampaignSchema,
  LeadEmailBrandAssets,
  LeadEmailReplySchema,
  LeadGroupSchema,
  LeadSchema,
  LeadStatsSchema,
  LeadCampaignAudiencePreview,
} from '../../data/types';
import LeadsLayout from '../../layouts/LeadsLayout';
import { DeleteConfirmationModal } from '../../molecules/AppModal';
import {
  buildCampaignAudienceSnapshot,
  buildLeadGroupFilterSnapshot,
  buildLeadListFilterParams,
  campaignResendConfirmMessage,
  countSelectedRecipients,
  DEFAULT_DRAFT_LEAD_FILTER,
  DEFAULT_LEAD_FILTERS,
  DEFAULT_TEST_LEAD_FILTER,
  describeNewsletterRecipients,
  describeRecipientSelection,
  DRAFT_LEAD_FILTER_OPTIONS,
  DraftLeadFilter,
  filtersFromLeadGroup,
  formatCampaignDeliverySummary,
  formatLeadApiErrorMessage,
  formatLeadCityOrCounty,
  formatLeadFriendlyTimestamp,
  formatLeadFullTimestamp,
  formatLeadPaginationLabel,
  formatLeadStateAbbrev,
  groupLeadsByField,
  hasActiveLeadFilters,
  isLeadsAdminEmail,
  isTerminalCampaignStatus,
  labelForLeadCategory,
  labelForLeadStage,
  labelForLeadStatus,
  LEAD_CATEGORY_OPTIONS,
  LEAD_DEFAULT_TIMEZONE,
  LEAD_STAGE_OPTIONS,
  LEAD_STATUS_OPTIONS,
  LeadGroupByKey,
  LeadRecipientSelection,
  PAGE_SIZE_OPTIONS,
  pinnedLeadGroupsFromList,
  TEST_LEAD_FILTER_OPTIONS,
  TestLeadFilter,
  US_STATE_OPTIONS,
  waitMs,
} from '../../utils/leadsAdmin';
import {
  loadLeadsAdminFilters,
  saveLeadsAdminFilters,
} from '../../utils/leadsAdminFilterStorage';
import {
  loadLeadsAdminBrandAssets,
  saveLeadsAdminBrandAssets,
} from '../../utils/leadsAdminBrandStorage';
import { DEFAULT_LEAD_EMAIL_BRAND_ASSETS } from '../../utils/leadEmailHtml';

type TabKey = 'all' | 'food_entrepreneur' | 'resource_provider' | 'groups';

type SortKey =
  | 'business_name'
  | 'email'
  | 'first_name'
  | 'lead_category'
  | 'lead_stage'
  | 'lead_status'
  | 'city'
  | 'county'
  | 'state'
  | 'business_zip_code'
  | 'last_contacted'
  | 'created';

const SEARCH_DEBOUNCE_MS = 400;
const BRAND_SAVE_DEBOUNCE_MS = 500;
const FILTER_SAVE_DEBOUNCE_MS = 300;
const CAMPAIGN_POLL_MS = 1500;
const CAMPAIGN_PAGE_SIZE = 5;
const TABLE_COLSPAN = 12;
const EMPTY_RECIPIENT_SELECTION: LeadRecipientSelection = {
  mode: 'none',
  selectedLeadIds: [],
  excludedLeadIds: [],
};

/** Referral / seminar flag for CRM rows linked to the AiriWheels pipeline. */
const referralFlagForLead = (lead: LeadSchema) => {
  if (lead.signup_source === 'joint_seminar') return { label: 'Seminar', tone: 'referral' as const };
  if (lead.signup_source === 'pantry_referral' || lead.referral_id) return { label: 'Referral', tone: 'referral' as const };
  return null;
};

const statCardClass =
  'rounded-lg border border-[#E9E9E9] bg-white px-4 py-3 shadow-sm min-w-[140px]';

/** Staff-only CRM dashboard for landing-page leads and partner pipeline tracking. */
const LeadsAdmin: FC = () => {
  const { authUser, primaryCommunity } = useAuthState();
  const location = useLocation();
  const adminEmail = authUser?.email ?? authUser?.username ?? '';
  const communityTimezone = primaryCommunity?.timezone || LEAD_DEFAULT_TIMEZONE;
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const leadIdFromQuery = queryParams.get('lead_id');
  const referralIdFromQuery = queryParams.get('referral_id');

  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [stats, setStats] = useState<LeadStatsSchema | null>(null);
  const [leads, setLeads] = useState<LeadSchema[]>([]);
  const [groups, setGroups] = useState<LeadGroupSchema[]>([]);
  const [tab, setTab] = useState<TabKey>('all');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedLead, setSelectedLead] = useState<LeadSchema | null>(null);
  const [draft, setDraft] = useState<Partial<LeadSchema>>({});
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailUseHomecookTemplate, setEmailUseHomecookTemplate] = useState(true);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [campaigns, setCampaigns] = useState<LeadCampaignSchema[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsLoadingMore, setCampaignsLoadingMore] = useState(false);
  const [campaignsPage, setCampaignsPage] = useState(1);
  const [campaignsTotal, setCampaignsTotal] = useState(0);
  const [campaignsPages, setCampaignsPages] = useState(1);
  const [campaignsStatusFilter, setCampaignsStatusFilter] = useState('');
  const [showComposer, setShowComposer] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<LeadCampaignSchema | null>(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [resendTarget, setResendTarget] = useState<LeadCampaignSchema | null>(null);
  const [pendingSendDraft, setPendingSendDraft] = useState<{
    campaignId?: string;
    name: string;
    subject: string;
    messageHtml: string;
    useHomecookTemplate: boolean;
    brandAssets?: LeadEmailBrandAssets;
    audience: ReturnType<typeof buildCampaignAudienceSnapshot>;
  } | null>(null);
  const [brandAssets, setBrandAssets] = useState<LeadEmailBrandAssets>(DEFAULT_LEAD_EMAIL_BRAND_ASSETS);
  const [brandHydrated, setBrandHydrated] = useState(false);
  const [unreadReplyCount, setUnreadReplyCount] = useState(0);
  const [replyInbox, setReplyInbox] = useState<LeadEmailReplySchema[]>([]);
  const [replyInboxTotal, setReplyInboxTotal] = useState(0);
  const [showRepliesPanel, setShowRepliesPanel] = useState(true);
  const replyUnreadOnlyRef = useRef(true);
  const [sendSubmitting, setSendSubmitting] = useState(false);
  const [deleteLeadTarget, setDeleteLeadTarget] = useState<LeadSchema | null>(null);
  const [deleteGroupTarget, setDeleteGroupTarget] = useState<LeadGroupSchema | null>(null);
  const [groupName, setGroupName] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [sort, setSort] = useState<SortKey>('created');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [groupBy, setGroupBy] = useState<LeadGroupByKey>('none');
  const [filterStage, setFilterStage] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterSignupSource, setFilterSignupSource] = useState('');
  const [filterResourceType, setFilterResourceType] = useState('');
  const [testLeadFilter, setTestLeadFilter] = useState<TestLeadFilter>(DEFAULT_TEST_LEAD_FILTER);
  const [draftLeadFilter, setDraftLeadFilter] = useState<DraftLeadFilter>(DEFAULT_DRAFT_LEAD_FILTER);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [recipientSelection, setRecipientSelection] = useState<LeadRecipientSelection>(EMPTY_RECIPIENT_SELECTION);
  const [audiencePreview, setAudiencePreview] = useState<LeadCampaignAudiencePreview | null>(null);
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const [focusLeadEmailPanel, setFocusLeadEmailPanel] = useState(false);
  const leadEmailFormRef = useRef<HTMLFormElement>(null);

  const categoryFilter = tab === 'food_entrepreneur' || tab === 'resource_provider' ? tab : undefined;

  const listFilterParams = useMemo(
    () =>
      buildLeadListFilterParams({
        debouncedSearch,
        categoryFilter,
        filterStage,
        filterStatus,
        filterState,
        filterSignupSource,
        filterResourceType,
        draftLeadFilter,
        testLeadFilter,
        activeGroupId,
        page,
        pageSize,
        sort,
        dir,
      }),
    [
      activeGroupId,
      categoryFilter,
      debouncedSearch,
      dir,
      draftLeadFilter,
      filterStage,
      filterState,
      filterStatus,
      filterSignupSource,
      filterResourceType,
      page,
      pageSize,
      sort,
      testLeadFilter,
    ]
  );

  const activeGroup = useMemo(
    () => groups.find((group) => group.id === activeGroupId) ?? null,
    [activeGroupId, groups]
  );

  const pinnedGroups = useMemo(() => pinnedLeadGroupsFromList(groups), [groups]);

  const filtersActive = hasActiveLeadFilters(
    {
      searchInput,
      filterStage,
      filterStatus,
      filterState,
      filterSignupSource,
      filterResourceType,
      draftLeadFilter,
      testLeadFilter,
      activeGroupId,
    },
    categoryFilter
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!adminEmail) {
      setFiltersHydrated(true);
      return;
    }
    if (filtersHydrated) return;
    const saved = loadLeadsAdminFilters(adminEmail);
    setTab(saved.tab);
    setSearchInput(saved.searchInput);
    setDebouncedSearch(saved.searchInput.trim());
    setFilterStage(saved.filterStage);
    setFilterStatus(saved.filterStatus);
    setFilterState(saved.filterState);
    setDraftLeadFilter(saved.draftLeadFilter);
    setTestLeadFilter(saved.testLeadFilter);
    setPageSize(saved.pageSize);
    setSort(saved.sort as SortKey);
    setDir(saved.dir);
    setGroupBy(saved.groupBy);
    setShowFilters(saved.showFilters);
    setFiltersHydrated(true);
  }, [adminEmail, filtersHydrated]);

  useEffect(() => {
    if (!adminEmail || !filtersHydrated) return;
    const timer = setTimeout(() => {
      saveLeadsAdminFilters(adminEmail, {
        tab,
        searchInput,
        filterStage,
        filterStatus,
        filterState,
        draftLeadFilter,
        testLeadFilter,
        pageSize,
        sort,
        dir,
        groupBy,
        showFilters,
      });
    }, FILTER_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [
    adminEmail,
    debouncedSearch,
    dir,
    draftLeadFilter,
    filterStage,
    filterState,
    filterStatus,
    filtersHydrated,
    groupBy,
    pageSize,
    searchInput,
    showFilters,
    sort,
    tab,
    testLeadFilter,
  ]);

  useEffect(() => {
    if (!adminEmail) {
      setBrandHydrated(true);
      return;
    }
    if (brandHydrated) return;
    const local = loadLeadsAdminBrandAssets(adminEmail);
    fetchLeadBrandDefaults(adminEmail)
      .then((remote) => {
        setBrandAssets(remote?.brand_assets ?? local);
      })
      .catch(() => {
        setBrandAssets(local);
      })
      .finally(() => setBrandHydrated(true));
  }, [adminEmail, brandHydrated]);

  useEffect(() => {
    if (!adminEmail || !brandHydrated) return;
    const timer = setTimeout(() => {
      saveLeadsAdminBrandAssets(adminEmail, brandAssets);
      saveLeadBrandDefaults(brandAssets, adminEmail).catch(() => undefined);
    }, BRAND_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [adminEmail, brandAssets, brandHydrated]);

  useEffect(() => {
    setRecipientSelection(EMPTY_RECIPIENT_SELECTION);
  }, [activeGroupId, debouncedSearch, filterStage, filterStatus, filterState, filterSignupSource, filterResourceType, draftLeadFilter, testLeadFilter, tab]);

  const isInitialLoadRef = useRef(true);

  const refreshGroups = useCallback(async () => {
    const groupsData = await fetchLeadGroups(adminEmail);
    setGroups(groupsData?.items ?? []);
  }, [adminEmail]);

  const refreshCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    try {
      const data = await fetchLeadCampaigns(adminEmail, {
        page: 1,
        limit: CAMPAIGN_PAGE_SIZE,
        ...(campaignsStatusFilter ? { status: campaignsStatusFilter } : {}),
      });
      setCampaigns(data?.items ?? []);
      setCampaignsPage(1);
      setCampaignsTotal(data?.total ?? 0);
      setCampaignsPages(data?.pages ?? 1);
    } catch {
      setStatusMessage('Could not load campaigns.');
    } finally {
      setCampaignsLoading(false);
    }
  }, [adminEmail, campaignsStatusFilter]);

  const loadMoreCampaigns = useCallback(async () => {
    if (campaignsLoadingMore) return;
    const nextPage = campaignsPage + 1;
    setCampaignsLoadingMore(true);
    try {
      const data = await fetchLeadCampaigns(adminEmail, {
        page: nextPage,
        limit: CAMPAIGN_PAGE_SIZE,
        ...(campaignsStatusFilter ? { status: campaignsStatusFilter } : {}),
      });
      const incoming = data?.items ?? [];
      setCampaigns((current) => {
        const seen = new Set(current.map((campaign) => campaign.id).filter(Boolean));
        return [
          ...current,
          ...incoming.filter((campaign) => campaign.id && !seen.has(campaign.id)),
        ];
      });
      setCampaignsPage(data?.page ?? nextPage);
      setCampaignsTotal(data?.total ?? campaignsTotal);
      setCampaignsPages(data?.pages ?? campaignsPages);
    } catch {
      setStatusMessage('Could not load more campaigns.');
    } finally {
      setCampaignsLoadingMore(false);
    }
  }, [
    adminEmail,
    campaignsLoadingMore,
    campaignsPage,
    campaignsPages,
    campaignsStatusFilter,
    campaignsTotal,
  ]);

  const loadData = useCallback(async () => {
    if (!filtersHydrated) return;
    if (!isLeadsAdminEmail(adminEmail)) {
      setAllowed(false);
      setInitialLoading(false);
      setTableLoading(false);
      return;
    }

    setTableLoading(true);
    const isInitialLoad = isInitialLoadRef.current;
    try {
      const access = await checkLeadsAdminAccess(adminEmail);
      if (!access?.allowed) {
        setAllowed(false);
        return;
      }
      setAllowed(true);

      const statsParams = { ...listFilterParams };
      delete statsParams.page;
      delete statsParams.limit;
      delete statsParams.sort;
      delete statsParams.dir;

      const [statsData, leadsData, groupsData] = await Promise.all([
        fetchLeadStats(adminEmail, statsParams),
        fetchLeads({ adminEmail, ...listFilterParams }),
        fetchLeadGroups(adminEmail),
      ]);
      setStats(statsData);
      setLeads(leadsData?.items ?? []);
      setTotal(leadsData?.total ?? 0);
      setPages(leadsData?.pages ?? 1);

      try {
        const replyData = await fetchLeadEmailReplies(adminEmail, {
          limit: 25,
          ...(replyUnreadOnlyRef.current ? { unread: true } : {}),
        });
        setReplyInbox(replyData?.items ?? []);
        setReplyInboxTotal(replyData?.total ?? 0);
        setUnreadReplyCount(replyData?.unread_total ?? 0);
      } catch (replyError) {
        console.error('Failed to load lead reply inbox during dashboard load', replyError);
      }
      // Re-fetch groups after a group-scoped leads query so stale pinned ids pruned by the API are reflected in the banner.
      if (listFilterParams.group_id) {
        const refreshedGroups = await fetchLeadGroups(adminEmail);
        setGroups(refreshedGroups?.items ?? []);
      } else {
        setGroups(groupsData?.items ?? []);
      }
    } catch {
      if (isInitialLoad) {
        setStatusMessage('Could not load leads dashboard.');
        setAllowed(false);
      }
    } finally {
      isInitialLoadRef.current = false;
      setInitialLoading(false);
      setTableLoading(false);
    }
  }, [adminEmail, filtersHydrated, listFilterParams]);

  const refreshReplyInbox = useCallback(async (options?: { unread?: boolean }) => {
    if (!adminEmail) return;
    if (options?.unread !== undefined) {
      replyUnreadOnlyRef.current = options.unread;
    }
    try {
      const data = await fetchLeadEmailReplies(adminEmail, {
        limit: 25,
        ...(replyUnreadOnlyRef.current ? { unread: true } : {}),
      });
      setReplyInbox(data?.items ?? []);
      setReplyInboxTotal(data?.total ?? 0);
      setUnreadReplyCount(data?.unread_total ?? 0);
    } catch (error) {
      console.error('Failed to load lead reply inbox', error);
    }
  }, [adminEmail]);

  const pageLeadIds = useMemo(
    () => leads.map((lead) => lead.id).filter((id): id is string => Boolean(id)),
    [leads]
  );

  const isLeadSelected = useCallback(
    (leadId?: string) => {
      if (!leadId) return false;
      if (recipientSelection.mode === 'all_filtered') {
        return !recipientSelection.excludedLeadIds.includes(leadId);
      }
      if (recipientSelection.mode === 'manual') {
        return recipientSelection.selectedLeadIds.includes(leadId);
      }
      return false;
    },
    [recipientSelection]
  );

  const clearRecipientSelection = () => setRecipientSelection(EMPTY_RECIPIENT_SELECTION);

  const toggleLeadSelection = (leadId?: string) => {
    if (!leadId) return;
    setRecipientSelection((current) => {
      if (current.mode === 'all_filtered') {
        const excluded = new Set(current.excludedLeadIds);
        if (excluded.has(leadId)) excluded.delete(leadId);
        else excluded.add(leadId);
        return { ...current, excludedLeadIds: Array.from(excluded) };
      }
      const selected = new Set(current.selectedLeadIds);
      if (selected.has(leadId)) selected.delete(leadId);
      else selected.add(leadId);
      return {
        mode: selected.size ? 'manual' : 'none',
        selectedLeadIds: Array.from(selected),
        excludedLeadIds: [],
      };
    });
  };

  const toggleSelectPage = () => {
    const allPageSelected =
      pageLeadIds.length > 0 && pageLeadIds.every((leadId) => isLeadSelected(leadId));
    if (allPageSelected) {
      setRecipientSelection(EMPTY_RECIPIENT_SELECTION);
      return;
    }
    setRecipientSelection({
      mode: 'manual',
      selectedLeadIds: pageLeadIds,
      excludedLeadIds: [],
    });
  };

  const toggleSelectAllFiltered = () => {
    if (recipientSelection.mode === 'all_filtered') {
      clearRecipientSelection();
      return;
    }
    setRecipientSelection({
      mode: 'all_filtered',
      selectedLeadIds: [],
      excludedLeadIds: [],
    });
  };

  const handleHeaderSelectionToggle = () => {
    if (recipientSelection.mode === 'none') {
      toggleSelectPage();
      return;
    }
    if (recipientSelection.mode === 'manual') {
      toggleSelectAllFiltered();
      return;
    }
    clearRecipientSelection();
  };

  const headerCheckboxChecked =
    recipientSelection.mode === 'all_filtered' ||
    (recipientSelection.mode === 'manual' &&
      pageLeadIds.length > 0 &&
      pageLeadIds.every((leadId) => recipientSelection.selectedLeadIds.includes(leadId)));

  const headerCheckboxIndeterminate =
    recipientSelection.mode === 'manual' &&
    recipientSelection.selectedLeadIds.length > 0 &&
    !headerCheckboxChecked;

  const selectionActive = recipientSelection.mode !== 'none';

  useEffect(() => {
    loadData();
  }, [loadData, filtersHydrated]);

  useEffect(() => {
    if (allowed) refreshCampaigns();
  }, [allowed, refreshCampaigns]);

  useEffect(() => {
    if (selectedLead) setDraft(selectedLead);
  }, [selectedLead]);

  useEffect(() => {
    if (!selectedLead || !focusLeadEmailPanel) return;
    leadEmailFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setFocusLeadEmailPanel(false);
  }, [focusLeadEmailPanel, selectedLead]);

  const campaignAudience = useMemo(
    () =>
      buildCampaignAudienceSnapshot({
        debouncedSearch,
        categoryFilter,
        filterStage,
        filterStatus,
        filterState,
        filterSignupSource,
        filterResourceType,
        draftLeadFilter,
        testLeadFilter,
        activeGroupId,
        activeGroupName: activeGroup?.name,
        selection: recipientSelection,
        resolvedCount: countSelectedRecipients({ selection: recipientSelection, totalFiltered: total }),
      }),
    [
      activeGroup?.name,
      activeGroupId,
      categoryFilter,
      debouncedSearch,
      draftLeadFilter,
      filterStage,
      filterState,
      filterStatus,
      filterSignupSource,
      filterResourceType,
      recipientSelection,
      testLeadFilter,
      total,
    ]
  );

  const effectiveRecipientCount = audiencePreview?.total ?? countSelectedRecipients({
    selection: recipientSelection,
    totalFiltered: total,
  });

  const newsletterRecipientSummary = useMemo(() => {
    const selectionLabel = describeRecipientSelection({
      selection: recipientSelection,
      totalFiltered: total,
      activeGroupName: activeGroup?.name,
    });
    if (selectionLabel) {
      return `Recipients: ${selectionLabel}.`;
    }
    return describeNewsletterRecipients({
      total: effectiveRecipientCount,
      activeGroupName: activeGroup?.name,
      sampleLeads: leads,
    });
  }, [activeGroup?.name, effectiveRecipientCount, leads, recipientSelection, total]);

  useEffect(() => {
    if (!allowed || !adminEmail) return;
    previewLeadCampaignAudience(campaignAudience, adminEmail)
      .then((preview) => setAudiencePreview(preview))
      .catch(() => setAudiencePreview(null));
  }, [adminEmail, allowed, campaignAudience, showComposer, showSendModal]);

  const openLeadEditor = useCallback((lead: LeadSchema) => {
    setSelectedLead(lead);
    setFocusLeadEmailPanel(false);
  }, []);

  const openLeadEmailPanel = useCallback((lead: LeadSchema) => {
    setSelectedLead(lead);
    setFocusLeadEmailPanel(true);
  }, []);

  useEffect(() => {
    if ((!leadIdFromQuery && !referralIdFromQuery) || !leads.length) return;
    const match = leads.find((lead) => lead.id === leadIdFromQuery)
      || leads.find((lead) => Boolean(referralIdFromQuery) && lead.referral_id === referralIdFromQuery);
    if (match) setSelectedLead(match);
  }, [leadIdFromQuery, leads, referralIdFromQuery]);

  const summaryCards = useMemo(
    () => [
      { label: 'Total leads', value: stats?.total ?? 0 },
      { label: 'New leads', value: stats?.by_status?.new_lead ?? 0 },
      { label: 'Qualified', value: stats?.qualified_count ?? 0 },
      { label: 'Hot leads', value: stats?.by_status?.hot_lead ?? 0 },
      { label: '% Qualified', value: `${stats?.pct_qualified ?? 0}%` },
      { label: 'Onboarding stage', value: stats?.by_stage?.onboarding ?? 0 },
    ],
    [stats]
  );

  const groupedSections = useMemo(() => {
    const labelResolver =
      groupBy === 'lead_stage'
        ? labelForLeadStage
        : groupBy === 'lead_status'
          ? labelForLeadStatus
          : groupBy === 'lead_category'
            ? labelForLeadCategory
            : (value?: string) => value ?? '-';
    return groupLeadsByField(leads, groupBy, labelResolver);
  }, [groupBy, leads]);

  const toggleSort = (field: SortKey) => {
    if (sort === field) {
      setDir((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSort(field);
    setDir('asc');
    setPage(1);
  };

  const sortIndicator = (field: SortKey) => (sort === field ? (dir === 'asc' ? ' ↑' : ' ↓') : '');

  const clearFilters = () => {
    setSearchInput(DEFAULT_LEAD_FILTERS.searchInput);
    setDebouncedSearch('');
    setFilterStage(DEFAULT_LEAD_FILTERS.filterStage);
    setFilterStatus(DEFAULT_LEAD_FILTERS.filterStatus);
    setFilterState(DEFAULT_LEAD_FILTERS.filterState);
    setFilterSignupSource('');
    setFilterResourceType('');
    setDraftLeadFilter(DEFAULT_LEAD_FILTERS.draftLeadFilter);
    setTestLeadFilter(DEFAULT_LEAD_FILTERS.testLeadFilter);
    setActiveGroupId(DEFAULT_LEAD_FILTERS.activeGroupId);
    setPage(1);
  };

  /** Apply a saved group's filters and open its members in the leads table. */
  const handleViewGroup = (group: LeadGroupSchema) => {
    const restored = filtersFromLeadGroup(group);
    setSearchInput(restored.searchInput ?? '');
    setDebouncedSearch(restored.searchInput?.trim() ?? '');
    setFilterStage(restored.filterStage ?? '');
    setFilterStatus(restored.filterStatus ?? '');
    setFilterState(restored.filterState ?? '');
    setDraftLeadFilter(restored.draftLeadFilter ?? DEFAULT_DRAFT_LEAD_FILTER);
    setTestLeadFilter(restored.testLeadFilter ?? DEFAULT_TEST_LEAD_FILTER);
    setActiveGroupId(group.id ?? null);

    const groupCategory = group.filter?.lead_category;
    if (groupCategory === 'food_entrepreneur' || groupCategory === 'resource_provider') {
      setTab(groupCategory);
    } else {
      setTab('all');
    }
    setPage(1);
  };

  /** Pin or unpin a saved group on the tab bar (persisted on the group document). */
  const handleTogglePinGroup = async (group: LeadGroupSchema) => {
    if (!group.id) return;
    const nextPinned = !group.pinned_to_tab_bar;
    try {
      const updated = await updateLeadGroupMembers(
        group.id,
        { pinned_to_tab_bar: nextPinned },
        adminEmail
      );
      if (updated) {
        setGroups((current) =>
          current.map((item) => (item.id === group.id ? { ...item, ...updated } : item))
        );
      } else {
        await refreshGroups();
      }
      setStatusMessage(
        nextPinned ? `${group.name} pinned to tab bar.` : `${group.name} unpinned from tab bar.`
      );
    } catch {
      setStatusMessage(`Could not update pin for ${group.name}.`);
    }
  };

  const handleToggleGroupMembership = async (group: LeadGroupSchema, include: boolean) => {
    if (!selectedLead?.id || !group.id) return;
    try {
      await updateLeadGroupMembers(
        group.id,
        include ? { add: [selectedLead.id] } : { remove: [selectedLead.id] },
        adminEmail
      );
      await refreshGroups();
      await loadData();
      setStatusMessage(include ? `Added to ${group.name}.` : `Removed from ${group.name}.`);
    } catch {
      setStatusMessage(`Could not update ${group.name}.`);
    }
  };

  const renderLocationCell = (value?: string) => (
    <td className="max-w-[88px] truncate px-3 py-3 text-xs" title={value && value !== '-' ? value : undefined}>
      {value || '-'}
    </td>
  );

  const handleSaveLead = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedLead?.id) return;
    await updateLead(selectedLead.id, draft, adminEmail);
    setStatusMessage('Lead updated.');
    setSelectedLead(null);
    await loadData();
  };

  const handleEmailLead = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedLead?.id || emailSubmitting) return;

    const subject = emailSubject.trim();
    if (!subject) {
      setStatusMessage('Email subject is required.');
      return;
    }
    if (isLeadEmailBodyEmpty(emailMessage)) {
      setStatusMessage('Email message body is required.');
      return;
    }

    setEmailSubmitting(true);
    try {
      const result = await emailLead(
        {
          lead_id: selectedLead.id,
          subject,
          message: emailMessage,
          use_homecook_template: emailUseHomecookTemplate,
          brand_assets: brandAssets,
        },
        adminEmail
      );
      setStatusMessage(
        result?.simulated
          ? `Test lead - email simulated for ${selectedLead.email || selectedLead.business_name}.`
          : `Email sent to ${selectedLead.email}.`
      );
      setEmailSubject('');
      setEmailMessage('');
      await loadData();
    } catch (error) {
      setStatusMessage(formatLeadApiErrorMessage(error, 'Could not send email. Please try again.'));
    } finally {
      setEmailSubmitting(false);
    }
  };

  const pollCampaignUntilTerminal = useCallback(
    async (campaignId: string): Promise<LeadCampaignSchema | null> => {
      const doc = await fetchLeadCampaign(campaignId, adminEmail);
      if (!doc || isTerminalCampaignStatus(doc.status)) return doc;
      await waitMs(CAMPAIGN_POLL_MS);
      return pollCampaignUntilTerminal(campaignId);
    },
    [adminEmail]
  );

  const handleConfirmSendCampaign = async () => {
    if (!pendingSendDraft || sendSubmitting) return;
    setSendSubmitting(true);
    try {
      let campaignId = pendingSendDraft.campaignId;
      const payload = {
        name: pendingSendDraft.name,
        subject: pendingSendDraft.subject,
        message_html: pendingSendDraft.messageHtml,
        use_homecook_template: pendingSendDraft.useHomecookTemplate,
        brand_assets: pendingSendDraft.brandAssets ?? brandAssets,
        audience: pendingSendDraft.audience,
      };
      const saved = campaignId
        ? await updateLeadCampaign(campaignId, payload, adminEmail)
        : await createLeadCampaign(payload, adminEmail);
      campaignId = saved?.id ?? campaignId;
      if (!campaignId) throw new Error('Campaign not saved');

      const sent = await sendLeadCampaign(campaignId, adminEmail);
      const finalDoc =
        sent?.status === 'sending'
          ? await pollCampaignUntilTerminal(campaignId)
          : sent;

      setStatusMessage(
        `Campaign sent. ${formatCampaignDeliverySummary(finalDoc?.delivery_stats ?? sent?.delivery_stats, finalDoc?.engagement_stats ?? sent?.engagement_stats)}`
      );
      setShowSendModal(false);
      setShowComposer(false);
      setPendingSendDraft(null);
      setEditingCampaign(null);
      await refreshCampaigns();
    } catch (error) {
      setStatusMessage(formatLeadApiErrorMessage(error, 'Could not send campaign. Please try again.'));
    } finally {
      setSendSubmitting(false);
    }
  };

  const handleRequestSendFromComposer = (draft: {
    campaignId?: string;
    name: string;
    subject: string;
    messageHtml: string;
    useHomecookTemplate: boolean;
    brandAssets?: LeadEmailBrandAssets;
    audience: ReturnType<typeof buildCampaignAudienceSnapshot>;
  }) => {
    setPendingSendDraft(draft);
    setShowSendModal(true);
  };

  const handleDeleteLead = async (leadId: string) => {
    try {
      await deleteLead(leadId, adminEmail);
      setDeleteLeadTarget(null);
      if (selectedLead?.id === leadId) setSelectedLead(null);
      setStatusMessage('Lead deleted.');
      await loadData();
    } catch (error) {
      setStatusMessage(formatLeadApiErrorMessage(error, 'Could not delete lead.'));
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    try {
      await deleteLeadGroup(groupId, adminEmail);
      setDeleteGroupTarget(null);
      if (activeGroupId === groupId) setActiveGroupId(null);
      setStatusMessage('Lead group deleted.');
      await refreshGroups();
      await loadData();
    } catch (error) {
      setStatusMessage(formatLeadApiErrorMessage(error, 'Could not delete lead group.'));
    }
  };

  const handleDuplicateCampaign = (campaign: LeadCampaignSchema) => {
    setEditingCampaign({
      ...campaign,
      id: undefined,
      status: 'draft',
      name: `${campaign.name || 'Campaign'} (copy)`,
      audience: undefined,
      delivery_stats: undefined,
      engagement_stats: undefined,
      recipient_count: undefined,
      failures: undefined,
      sent_at: undefined,
      completed_at: undefined,
    });
    setShowComposer(true);
  };

  const handleResendCampaign = (campaign: LeadCampaignSchema) => {
    if (!campaign.id || sendSubmitting) return;
    setResendTarget(campaign);
  };

  const handleConfirmResendCampaign = async () => {
    if (!resendTarget?.id || sendSubmitting) return;
    setSendSubmitting(true);
    try {
      const sent = await resendLeadCampaign(resendTarget.id, adminEmail);
      const finalDoc =
        sent?.status === 'sending' ? await pollCampaignUntilTerminal(resendTarget.id) : sent;
      setStatusMessage(
        `Campaign resent. ${formatCampaignDeliverySummary(finalDoc?.delivery_stats ?? sent?.delivery_stats, finalDoc?.engagement_stats ?? sent?.engagement_stats)}`
      );
      setResendTarget(null);
      await refreshCampaigns();
    } catch (error) {
      setStatusMessage(formatLeadApiErrorMessage(error, 'Could not resend campaign. Please try again.'));
    } finally {
      setSendSubmitting(false);
    }
  };

  const handleCreateGroup = async (event: FormEvent) => {
    event.preventDefault();
    if (!groupName.trim()) return;
    try {
      await createLeadGroup(
        {
          name: groupName.trim(),
          description: 'Saved from current CRM view',
          filter: buildLeadGroupFilterSnapshot({
            categoryFilter,
            filterStage,
            filterStatus,
            filterState,
            draftLeadFilter,
            testLeadFilter,
          }),
          search_q: debouncedSearch,
          lead_ids: [],
        },
        adminEmail
      );
      setGroupName('');
      setStatusMessage('Lead group saved.');
      await refreshGroups();
    } catch {
      setStatusMessage('Could not save lead group.');
    }
  };

  const renderLeadRow = (lead: LeadSchema) => {
    const referralFlag = referralFlagForLead(lead);
    return (
    <tr key={lead.id} className="border-t border-[#F0F0F0] font-open-sans">
      <td className="px-3 py-3">
        <input
          type="checkbox"
          aria-label={`Select ${lead.business_name || lead.email || 'lead'}`}
          checked={isLeadSelected(lead.id)}
          onChange={() => toggleLeadSelection(lead.id)}
        />
      </td>
      <td className="px-4 py-3 font-semibold cursor-pointer" onClick={() => openLeadEditor(lead)}>
        {lead.business_name || '-'}
        {lead.signup_source === 'pawdit_demo' ? <LeadFlagBadge label="Pawdit" tone="pawdit" /> : null}
        {referralFlag ? <LeadFlagBadge label={referralFlag.label} tone="referral" /> : null}
        {lead.is_test_lead ? <LeadFlagBadge label="Test" /> : null}
        {lead.pending_email ? <LeadFlagBadge label="Draft" /> : null}
        {lead.last_replied_at ? <LeadFlagBadge label="Replied" /> : null}
      </td>
      <td className="px-4 py-3 cursor-pointer" onClick={() => openLeadEditor(lead)}>
        <div>{`${lead.first_name ?? ''} ${lead.last_name ?? ''}`.trim() || '-'}</div>
        <div className="text-xs text-[#737373]">{lead.email || 'Pending email'}</div>
      </td>
      <td className="px-4 py-3 cursor-pointer" onClick={() => openLeadEditor(lead)}>{labelForLeadCategory(lead.lead_category)}</td>
      <td className="px-4 py-3"><LeadStageBadge value={lead.lead_stage} /></td>
      <td className="px-4 py-3"><LeadStatusBadge value={lead.lead_status} /></td>
      {renderLocationCell(formatLeadCityOrCounty(lead.city))}
      {renderLocationCell(formatLeadCityOrCounty(lead.county))}
      {renderLocationCell(formatLeadStateAbbrev(lead.state))}
      {renderLocationCell(lead.business_zip_code)}
      <td className="px-4 py-3" title={formatLeadFullTimestamp(lead.last_contacted, communityTimezone) || undefined}>
        {lead.last_contacted ? formatLeadFriendlyTimestamp(lead.last_contacted, communityTimezone) : '-'}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-md bg-[#E6EBED] px-3 py-1 text-xs font-semibold"
            onClick={() => openLeadEditor(lead)}
          >
            Edit
          </button>
          <button
            type="button"
            className="rounded-md border border-[#e9e9e9] px-3 py-1 text-xs font-semibold text-[#013847]/80"
            onClick={() => openLeadEmailPanel(lead)}
          >
            Message
          </button>
          <button
            type="button"
            aria-label={`Delete ${lead.business_name || lead.email}`}
            className="rounded-md border border-[#FECACA] px-2 py-1 text-xs font-semibold text-[#DC2626]"
            onClick={() => setDeleteLeadTarget(lead)}
          >
            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
          </button>
        </div>
      </td>
    </tr>
    );
  };

  if (initialLoading) {
    return (
      <LeadsLayout>
        <div className="flex h-full min-h-[60vh] items-center justify-center text-[#013847]">
          Loading leads dashboard…
        </div>
      </LeadsLayout>
    );
  }

  if (!allowed) {
    return (
      <LeadsLayout>
        <div className="flex h-full min-h-[60vh] flex-col items-center justify-center px-6 text-center text-[#013847]">
          <h1 className="mb-2 text-2xl font-raleway font-bold">Leads dashboard access restricted</h1>
          <p className="max-w-lg text-sm text-[#4D4D4D]">
            Only HomeCook staff on the leads allowlist can view this page. Signed in as{' '}
            <strong>{adminEmail || 'unknown'}</strong>.
          </p>
        </div>
      </LeadsLayout>
    );
  }

  return (
    <LeadsLayout>
      <div className="min-h-screen px-6 py-8 text-[#013847]">
        <div className="mb-6 mt-12 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm text-[#4D4D4D] font-raleway">
              Track food entrepreneurs and resource providers from landing signups.
              <span className="font-open-sans text-xs mx-2 text-[#777]">
                {stats?.as_of ? ` Updated ${stats.as_of}.` : ''}
              </span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="relative rounded-lg border border-[#E9E9E9] px-4 py-2 text-sm font-semibold font-raleway"
              onClick={() => setShowRepliesPanel((current) => !current)}
            >
              Replies
              {/* {replyInboxTotal > 0 ? (
                <span className="ml-2 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-[#013847] px-1.5 py-0.5 text-[10px] font-bold text-white">
                  {replyInboxTotal}
                </span>
              ) : null} */}
              {unreadReplyCount > 0 ? (
                <span className="ml-2 inline-flex min-w-[1.25rem] font-open-sans items-center justify-center rounded-full bg-[#DC2626] px-1.5 py-0.5 text-[10px] font-bold text-white">
                  {unreadReplyCount}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              className="rounded-lg border border-[#013847] px-4 py-2 text-sm font-semibold font-raleway"
              onClick={() => setShowCreateModal(true)}
            >
              Add lead
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg bg-[#013847] px-4 py-2 text-sm font-semibold text-white font-raleway"
              onClick={() => setShowImportModal(true)}
            >
              <UploadFileIcon sx={{ fontSize: 18 }} />
              Import CSV/XLSX
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg bg-[#5DB6CE] px-4 py-2 text-sm font-semibold text-white/90 font-raleway"
              onClick={() => {
                setEditingCampaign(null);
                setShowComposer(true);
              }}
            >
              <MailOutlineIcon sx={{ fontSize: 18 }} />
              Create campaign
            </button>
            <button
              type="button"
              aria-label="Refresh leads"
              title="Refresh leads"
              disabled={tableLoading}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-[#E9E9E9] bg-white text-[#013847] transition-colors hover:border-[#5DB6CE] hover:bg-[#EFF8FA] disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() => loadData()}
            >
              <RefreshIcon
                sx={{ fontSize: 20 }}
                className={tableLoading ? 'animate-spin' : undefined}
              />
            </button>
          </div>
        </div>

        {statusMessage ? (
          <p className="mb-4 rounded-lg font-raleway border border-[#CFE8EF] bg-[#EFF8FA] px-4 py-3 text-sm">{statusMessage}</p>
        ) : null}

        <div className="mb-6 flex flex-wrap gap-3">
          {summaryCards.map((card) => (
            <div key={card.label} className={statCardClass}>
              <p className="text-xs font-raleway font-semibold uppercase tracking-wide text-[#737373]">{card.label}</p>
              <p className="mt-1 text-xl font-light font-open-sans">{card.value}</p>
            </div>
          ))}
        </div>

        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {([
              ['all', 'All leads'],
              ['food_entrepreneur', 'Food entrepreneurs'],
              ['resource_provider', 'Resource providers'],
            ] as const).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`rounded-full px-4 py-2 text-sm font-semibold font-raleway ${
                  tab === key && !activeGroupId
                    ? 'bg-[#013847] text-white'
                    : 'bg-white text-[#013847] border border-[#E9E9E9]'
                }`}
                onClick={() => {
                  setTab(key);
                  if (activeGroupId) {
                    setActiveGroupId(null);
                  }
                  setPage(1);
                }}
              >
                {label}
              </button>
            ))}
            {pinnedGroups.map((group) => (
              <button
                key={group.id}
                type="button"
                title={`View ${group.name} in table`}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-raleway ${
                  activeGroupId === group.id
                    ? 'bg-[#5DB6CE] text-white'
                    : 'border border-[#CFE8EF] bg-[#EFF8FA] text-[#013847] hover:border-[#5DB6CE]'
                }`}
                onClick={() => handleViewGroup(group as LeadGroupSchema)}
              >
                <PushPinIcon sx={{ fontSize: 14 }} />
                {group.name}
              </button>
            ))}
            <span className="mx-0.5 hidden h-7 w-px shrink-0 bg-[#E9E9E9] sm:block" aria-hidden />
            <button
              type="button"
              className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold font-raleway transition-colors ${
                tab === 'groups'
                  ? 'bg-[#013847] text-white shadow-sm'
                  : 'border border-[#013847]/15 bg-[#F4F8F9] text-[#013847] hover:border-[#013847]/30 hover:bg-[#EAF2F4]'
              }`}
              onClick={() => {
                setTab('groups');
                setPage(1);
              }}
            >
              <FolderOutlinedIcon sx={{ fontSize: 15, opacity: tab === 'groups' ? 1 : 0.85 }} />
              Lead groups
            </button>
            {tab !== 'groups' ? (
              <button
                type="button"
                aria-label={showFilters ? 'Hide filters' : 'Show filters'}
                aria-pressed={showFilters}
                title={showFilters ? 'Hide filters' : 'Show filters'}
                className={`rounded-full p-2 transition-colors ${
                  showFilters
                    ? 'border border-[#013847] bg-[#013847] text-white shadow-sm'
                    : 'border border-[#E9E9E9] bg-white text-[#737373] hover:border-[#013847] hover:text-[#013847]'
                }`}
                onClick={() => setShowFilters((current) => !current)}
              >
                {showFilters ? (
                  <FilterAltIcon fontSize="small" />
                ) : (
                  <FilterAltOutlinedIcon fontSize="small" />
                )}
              </button>
            ) : null}
          </div>
          {tab !== 'groups' && tableLoading ? (
            <span className="text-xs text-[#737373] font-open-sans">Updating leads…</span>
          ) : null}
        </div>

        {tab !== 'groups' ? (
          <>
            {activeGroup ? (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#CFE8EF] bg-[#EFF8FA] px-4 py-3 text-sm font-open-sans">
                <div>
                  <span>
                    Viewing group: <strong>{activeGroup.name}</strong>
                    {` · ${total} ${total === 1 ? 'lead' : 'leads'} in view`}
               
                    {/* {activeGroup.lead_ids?.length
                      ? ` (${activeGroup.lead_ids.length} pinned)`
                      : ''} */}
                  </span>
                  {total <= 0 ? (
                    <p className="mt-1 text-xs text-[#737373]">
                      No leads match this group. Re-add members from a lead&apos;s edit drawer, or save the group again from a filtered view.
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="rounded-md border border-[#013847] px-3 py-1 text-xs font-semibold font-raleway"
                  onClick={() => setActiveGroupId(null)}
                >
                  Clear group view
                </button>
              </div>
            ) : null}

            {showFilters ? (
              <div className="mb-4 grid gap-3 rounded-xl border border-[#E9E9E9] bg-white p-4 md:grid-cols-4">
                <input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search name, email, business, city, county, zip, domain…"
                  className="rounded-lg border border-[#E9E9E9] px-4 py-2 font-open-sans md:col-span-4"
                />
                <select
                  value={filterStage}
                  onChange={(event) => {
                    setFilterStage(event.target.value);
                    setPage(1);
                  }}
                  className="rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
                >
                  <option value="">All stages</option>
                  {LEAD_STAGE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <select
                  value={filterStatus}
                  onChange={(event) => {
                    setFilterStatus(event.target.value);
                    setPage(1);
                  }}
                  className="rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
                >
                  <option value="">All statuses</option>
                  {LEAD_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <select
                  value={filterState}
                  onChange={(event) => {
                    setFilterState(event.target.value);
                    setPage(1);
                  }}
                  className="rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
                >
                  <option value="">All states & territories</option>
                  {US_STATE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <select
                  value={filterSignupSource}
                  onChange={(event) => {
                    setFilterSignupSource(event.target.value);
                    setPage(1);
                  }}
                  className="rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
                  data-testid="signup-source-filter"
                >
                  <option value="">All sources</option>
                  <option value="pantry_referral">Pantry referrals</option>
                  <option value="joint_seminar">Joint seminar</option>
                </select>
                <select
                  value={filterResourceType}
                  onChange={(event) => {
                    setFilterResourceType(event.target.value);
                    setPage(1);
                  }}
                  className="rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
                >
                  <option value="">All resource types</option>
                  <option value="funding">Funding</option>
                  <option value="kitchen">Kitchen</option>
                  <option value="training">Training</option>
                </select>
                <select
                  value={draftLeadFilter}
                  onChange={(event) => {
                    setDraftLeadFilter(event.target.value as DraftLeadFilter);
                    setPage(1);
                  }}
                  className="rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
                >
                  {DRAFT_LEAD_FILTER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <select
                  value={testLeadFilter}
                  onChange={(event) => {
                    setTestLeadFilter(event.target.value as TestLeadFilter);
                    setPage(1);
                  }}
                  className="rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
                >
                  {TEST_LEAD_FILTER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <select
                  value={groupBy}
                  onChange={(event) => setGroupBy(event.target.value as LeadGroupByKey)}
                  className="rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
                >
                  <option value="none">Group by: none</option>
                  <option value="lead_stage">Group by stage</option>
                  <option value="lead_status">Group by status</option>
                  <option value="lead_category">Group by category</option>
                  <option value="state">Group by state</option>
                  <option value="city">Group by city</option>
                </select>
                {filtersActive ? (
                  <button
                    type="button"
                    className="rounded-lg border border-[#E9E9E9] px-3 py-2 text-sm font-semibold font-raleway"
                    onClick={clearFilters}
                  >
                    Clear filters
                  </button>
                ) : null}
              </div>
            ) : null}

            {selectionActive ? (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#CFE8EF] bg-[#EFF8FA] px-4 py-3 text-sm font-open-sans">
                <span>
                  {describeRecipientSelection({
                    selection: recipientSelection,
                    totalFiltered: total,
                    activeGroupName: activeGroup?.name,
                  })}
                </span>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="rounded-md border border-[#013847] px-3 py-1 text-xs font-semibold font-raleway"
                    onClick={() => {
                      setEditingCampaign(null);
                      setShowComposer(true);
                    }}
                  >
                    Create campaign
                  </button>
                  <button
                    type="button"
                    className="rounded-md border border-[#E9E9E9] px-3 py-1 text-xs font-semibold font-raleway"
                    onClick={clearRecipientSelection}
                  >
                    Clear selection
                  </button>
                </div>
              </div>
            ) : null}

            <div className={`overflow-x-auto rounded-xl border border-[#E9E9E9] bg-white ${tableLoading ? 'opacity-60' : ''}`}>
              <table className="min-w-full text-left text-sm">
                <thead className="bg-[#013847] text-white">
                  <tr>
                    <th className="px-3 py-3 text-xs font-semibold font-raleway">
                      <input
                        type="checkbox"
                        aria-label="Select leads for campaign"
                        checked={headerCheckboxChecked}
                        ref={(input) => {
                          if (input) input.indeterminate = headerCheckboxIndeterminate;
                        }}
                        onChange={handleHeaderSelectionToggle}
                      />
                    </th>
                    {([
                      ['business_name', 'Business'],
                      ['first_name', 'Contact'],
                      ['lead_category', 'Category'],
                      ['lead_stage', 'Stage'],
                      ['lead_status', 'Status'],
                      ['city', 'City'],
                      ['county', 'County'],
                      ['state', 'State'],
                      ['business_zip_code', 'Zipcode'],
                      ['last_contacted', 'Last contacted'],
                    ] as const).map(([field, heading]) => (
                      <th key={field} className="px-3 py-3 text-xs font-semibold font-raleway">
                        <button type="button" onClick={() => toggleSort(field)}>
                          {heading}{sortIndicator(field)}
                        </button>
                      </th>
                    ))}
                    <th className="px-4 py-3 font-semibold font-raleway" />
                  </tr>
                </thead>
                <tbody>
                  {groupedSections.map((section) => (
                    <Fragment key={section.key}>
                      {groupBy !== 'none' ? (
                        <tr className="bg-[#F8FAFB]">
                          <td colSpan={TABLE_COLSPAN} className="px-4 py-2 text-sm font-semibold font-raleway">
                            {section.label} ({section.items.length})
                          </td>
                        </tr>
                      ) : null}
                      {section.items.map((lead) => renderLeadRow(lead))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm font-open-sans">
              <span>{formatLeadPaginationLabel({ page, pageSize, total })}</span>
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative inline-flex items-center">
                  <select
                    value={pageSize}
                    onChange={(event) => {
                      setPageSize(Number(event.target.value));
                      setPage(1);
                    }}
                    aria-label="Leads per page"
                    className="appearance-none rounded-lg border border-[#E9E9E9] bg-white py-2 pl-3 pr-9 font-open-sans text-sm"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size} leads per page
                      </option>
                    ))}
                  </select>
                  <ExpandMoreIcon
                    className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-base text-[#737373]"
                    aria-hidden
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={page <= 1 || tableLoading}
                    className="rounded-lg border border-[#E9E9E9] px-3 py-1 disabled:opacity-40"
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={page >= pages || tableLoading || total <= 0}
                    className="rounded-lg border border-[#E9E9E9] px-3 py-1 disabled:opacity-40"
                    onClick={() => setPage((current) => Math.min(pages, current + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-8 grid gap-6 lg:grid-cols-2 font-open-sans">
              {showRepliesPanel ? (
                <LeadEmailRepliesPanel
                  adminEmail={adminEmail}
                  inboxReplies={replyInbox}
                  inboxTotal={replyInboxTotal}
                  timezone={communityTimezone}
                  onRefresh={refreshReplyInbox}
                  onUnreadChange={setUnreadReplyCount}
                />
              ) : null}
              <LeadCampaignPanel
                adminEmail={adminEmail}
                campaigns={campaigns}
                timezone={communityTimezone}
                loading={campaignsLoading}
                loadingMore={campaignsLoadingMore}
                statusFilter={campaignsStatusFilter}
                hasMore={campaigns.length < campaignsTotal || campaignsPage < campaignsPages}
                showingCount={campaigns.length}
                totalCount={campaignsTotal}
                onRefresh={refreshCampaigns}
                onStatusFilterChange={setCampaignsStatusFilter}
                onSeeMore={loadMoreCampaigns}
                onEdit={(campaign) => {
                  setEditingCampaign(campaign);
                  setShowComposer(true);
                }}
                onDuplicate={handleDuplicateCampaign}
                onResend={handleResendCampaign}
              />
            </div>
          </>
        ) : (
          <div className="grid gap-6 lg:grid-cols-2">
            <form onSubmit={handleCreateGroup} className="rounded-xl border border-[#E9E9E9] bg-white p-5">
              <h2 className="mb-3 text-lg font-bold font-raleway">Create lead group</h2>
              <p className="mb-3 text-xs font-open-sans text-[#737373]">
                Saves the current filtered view (stage, status, state, drafts, test leads, and search).
              </p>
              <input
                value={groupName}
                onChange={(event) => setGroupName(event.target.value)}
                placeholder="Group name"
                className="mb-3 w-full rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
                required
              />
              <button type="submit" className="rounded-lg bg-[#013847] px-4 py-2 text-sm font-semibold font-raleway text-white">
                Save group
              </button>
            </form>
            <div className="rounded-xl border border-[#E9E9E9] bg-white p-5">
              <h2 className="mb-3 text-lg font-bold font-raleway">Saved groups</h2>
              <ul className="space-y-3">
                {groups.map((group) => {
                  const isPinned = Boolean(group.pinned_to_tab_bar);
                  return (
                    <li
                      key={group.id}
                      role="button"
                      tabIndex={0}
                      aria-label={`View ${group.name} in table`}
                      className="group cursor-pointer rounded-lg border border-[#F0F0F0] px-4 py-3 transition-colors hover:border-[#5DB6CE] hover:bg-[#EFF8FA] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5DB6CE]"
                      onClick={() => handleViewGroup(group)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleViewGroup(group);
                        }
                      }}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-sm font-raleway">{group.name}</p>
                          <p className="text-xs font-open-sans text-[#737373]">{group.description || 'No description'}</p>
                          <p className="mt-1 text-xs font-open-sans text-[#737373]">
                            {group.lead_ids?.length ?? 0} pinned member(s)
                            {group.search_q ? ` · search: "${group.search_q}"` : ''}
                          </p>
                          <p className="mt-1 text-xs font-semibold font-raleway text-[#5DB6CE] opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                            View in table
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                        <button
                          type="button"
                          aria-label={isPinned ? `Unpin ${group.name} from tab bar` : `Pin ${group.name} to tab bar`}
                          title={isPinned ? 'Unpin from tab bar' : 'Pin to tab bar'}
                          className={`rounded-md p-2 transition-colors ${
                            isPinned
                              ? 'bg-[#EFF8FA] text-[#5DB6CE] hover:bg-[#CFE8EF]'
                              : 'bg-[#E6EBED] text-[#737373] hover:bg-[#DDE4E7] hover:text-[#013847]'
                          }`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleTogglePinGroup(group);
                          }}
                        >
                          {isPinned ? (
                            <PushPinIcon fontSize="small" />
                          ) : (
                            <PushPinOutlinedIcon fontSize="small" />
                          )}
                        </button>
                        <button
                          type="button"
                          aria-label={`Delete ${group.name}`}
                          title="Delete group"
                          className="rounded-md p-2 text-[#DC2626] hover:bg-[#FEF2F2]"
                          onClick={(event) => {
                            event.stopPropagation();
                            setDeleteGroupTarget(group);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {selectedLead ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-6">
              <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold font-raleway">
                    {selectedLead.business_name}
                    {selectedLead.signup_source === 'pawdit_demo' ? <LeadFlagBadge label="Pawdit" tone="pawdit" /> : null}
                    {referralFlagForLead(selectedLead) ? <LeadFlagBadge label={referralFlagForLead(selectedLead)!.label} tone="referral" /> : null}
                    {selectedLead.is_test_lead ? <LeadFlagBadge label="Test" /> : null}
                  </h2>
                  <p className="text-sm text-[#737373] font-open-sans">{selectedLead.email || 'Draft - pending email'}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <LeadStageBadge value={selectedLead.lead_stage} />
                    <LeadStatusBadge value={selectedLead.lead_status} />
                  </div>
                </div>
                <div className="flex flex-row items-end gap-2">
                  <button
                    type="button"
                    className="flex text-sm font-semibold font-raleway text-[#DC2626] mr-2"
                    onClick={() => setDeleteLeadTarget(selectedLead)}
                  >
                    <DeleteOutlineIcon fontSize="small" className="my-auto text-[#DC2626]" /> Delete
                  </button>
                  <button type="button" onClick={() => setSelectedLead(null)} className="text-[#737373] flex text-sm font-semibold font-raleway">
                    <CloseIcon fontSize="small" className="my-auto " />
                    Close
                  </button>
                  
                </div>
              </div>

              <form onSubmit={handleSaveLead} className="grid gap-3 md:grid-cols-2">
                {([
                  ['lead_stage', 'Stage', LEAD_STAGE_OPTIONS],
                  ['lead_status', 'Status', LEAD_STATUS_OPTIONS],
                  ['lead_category', 'Category', LEAD_CATEGORY_OPTIONS],
                ] as const).map(([field, label, options]) => (
                  <label key={field} className="text-sm font-raleway">
                    {label}
                    <select
                      className="mt-1 w-full rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
                      value={(draft[field] as string) ?? ''}
                      onChange={(event) => setDraft((current) => ({ ...current, [field]: event.target.value }))}
                    >
                      {options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
                {([
                  ['email', 'Email'],
                  ['account_manager', 'Account manager (HomeCook)'],
                  ['city', 'City'],
                  ['county', 'County'],
                  ['business_zip_code', 'Zipcode'],
                  ['phone', 'Phone'],
                  ['job_title', 'Job title'],
                  ['next_step', 'Next step'],
                  ['goal', 'Goal'],
                ] as const).map(([field, label]) => (
                  <label key={field} className="text-sm font-raleway">
                    {label}
                    <input
                      className="mt-1 w-full rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
                      value={(draft[field] as string) ?? ''}
                      onChange={(event) => setDraft((current) => ({ ...current, [field]: event.target.value }))}
                    />
                  </label>
                ))}
                <label className="text-sm font-raleway">
                  State
                  <select
                    className="mt-1 w-full rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
                    value={(draft.state as string) ?? ''}
                    onChange={(event) => setDraft((current) => ({ ...current, state: event.target.value }))}
                  >
                    <option value="">Select state / territory</option>
                    {US_STATE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label} ({option.value})
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex items-center gap-2 text-sm font-open-sans md:col-span-2">
                  <input
                    type="checkbox"
                    checked={Boolean(draft.is_test_lead)}
                    onChange={(event) => setDraft((current) => ({ ...current, is_test_lead: event.target.checked }))}
                  />
                  Mark as test lead
                </label>
                {([
                  ['notes', 'Notes'],
                  ['provider_action_items', 'Action items by provider'],
                  ['homecook_action_items', 'Action items by HomeCook'],
                ] as const).map(([field, label]) => (
                  <label key={field} className="text-sm md:col-span-2 font-raleway">
                    {label}
                    <textarea
                      className="mt-1 min-h-[80px] w-full rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
                      value={(draft[field] as string) ?? ''}
                      onChange={(event) => setDraft((current) => ({ ...current, [field]: event.target.value }))}
                    />
                  </label>
                ))}
                <div className="md:col-span-2">
                  <button type="submit" className="rounded-lg bg-[#013847] px-4 py-2 text-sm font-semibold font-raleway text-white">
                    Save lead
                  </button>
                </div>
              </form>

              {groups.length ? (
                <div className="mt-6 border-t border-[#E9E9E9] pt-6">
                  <h3 className="mb-3 font-bold font-raleway">Lead groups</h3>
                  <ul className="space-y-2">
                    {groups.map((group) => {
                      const isMember = Boolean(
                        selectedLead?.id && group.lead_ids?.includes(selectedLead.id)
                      );
                      return (
                        <li key={group.id} className="flex items-center justify-between gap-3 text-sm font-open-sans">
                          <span>{group.name}</span>
                          <button
                            type="button"
                            className={`rounded-md px-3 py-1 text-xs font-semibold font-raleway ${
                              isMember ? 'border border-[#013847] text-[#013847]' : 'bg-[#E6EBED]'
                            }`}
                            onClick={() => handleToggleGroupMembership(group, !isMember)}
                          >
                            {isMember ? 'Remove' : 'Add'}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}

              <form ref={leadEmailFormRef} id="lead-email-form" onSubmit={handleEmailLead} className="mt-6 border-t border-[#E9E9E9] pt-6">
                <h3 className="mb-1 font-bold font-raleway">Email this lead</h3>
                <p className="mb-3 text-sm text-[#737373] font-open-sans">
                  Recipient: {selectedLead.email || 'Draft - pending email (simulated)'}
                  {selectedLead.business_name ? ` · ${selectedLead.business_name}` : ''}
                </p>
                {selectedLead.is_test_lead || selectedLead.pending_email ? (
                  <p className="mb-3 text-sm text-[#B45309] font-open-sans">
                    This lead is marked test or pending email - delivery will be simulated.
                  </p>
                ) : null}
                <LeadMergeTagSubjectInput
                  value={emailSubject}
                  onChange={setEmailSubject}
                  placeholder="Subject"
                  required
                  className="mb-3"
                  inputClassName="rounded-lg border border-[#E9E9E9] px-3 py-2"
                />
                <div className="mb-3">
                  <LeadEmailBodyEditor
                    value={emailMessage}
                    onChange={setEmailMessage}
                    placeholder="Message body. Use {{first_name}} for personalization."
                    minHeightClass="min-h-[100px]"
                  />
                </div>
                <label className="mb-3 flex items-start gap-2 text-sm font-open-sans">
                  <input
                    type="checkbox"
                    checked={emailUseHomecookTemplate}
                    onChange={(event) => setEmailUseHomecookTemplate(event.target.checked)}
                    className="mt-0.5"
                  />
                  <span>Wrap in HomeCook email template (logo, header, footer)</span>
                </label>
                <LeadEmailBrandPanel
                  adminEmail={adminEmail}
                  assets={brandAssets}
                  onChange={setBrandAssets}
                  compact
                />
                <div className="mt-4">
                  <LeadEmailRepliesPanel
                    adminEmail={adminEmail}
                    leadId={selectedLead.id}
                    timezone={communityTimezone}
                    compact
                  />
                </div>
                <button
                  type="submit"
                  disabled={emailSubmitting}
                  className="rounded-lg border border-[#013847] px-4 py-2 text-sm font-semibold font-raleway disabled:opacity-60"
                >
                  {emailSubmitting ? 'Sending…' : 'Send email'}
                </button>
              </form>
            </div>
          </div>
        ) : null}

        {showImportModal ? (
          <LeadImportModal
            adminEmail={adminEmail}
            onClose={() => setShowImportModal(false)}
            onComplete={(message) => {
              setStatusMessage(message);
              setShowImportModal(false);
              loadData();
            }}
          />
        ) : null}

        {showCreateModal ? (
          <LeadCreateModal
            adminEmail={adminEmail}
            onClose={() => setShowCreateModal(false)}
            onComplete={(message) => {
              setStatusMessage(message);
              setShowCreateModal(false);
              loadData();
            }}
          />
        ) : null}

        <LeadNewsletterComposer
          isOpen={showComposer}
          adminEmail={adminEmail}
          recipientSummary={newsletterRecipientSummary}
          audience={campaignAudience}
          audiencePreview={audiencePreview}
          initialCampaign={editingCampaign}
          brandAssets={brandAssets}
          onBrandAssetsChange={setBrandAssets}
          onClose={() => {
            setShowComposer(false);
            setEditingCampaign(null);
          }}
          onSaved={() => refreshCampaigns()}
          onRequestSend={handleRequestSendFromComposer}
        />

        <LeadNewsletterSendModal
          isOpen={showSendModal}
          recipientSummary={newsletterRecipientSummary}
          recipientCount={effectiveRecipientCount}
          groupName={pendingSendDraft?.audience?.group_name ?? activeGroup?.name}
          subject={pendingSendDraft?.subject ?? ''}
          useHomecookTemplate={pendingSendDraft?.useHomecookTemplate ?? true}
          submitting={sendSubmitting}
          onClose={() => {
            if (sendSubmitting) return;
            setShowSendModal(false);
            setPendingSendDraft(null);
          }}
          onConfirm={handleConfirmSendCampaign}
        />

        <DeleteConfirmationModal
          isOpen={Boolean(deleteLeadTarget)}
          data={deleteLeadTarget ? { id: deleteLeadTarget.id, name: deleteLeadTarget.business_name || deleteLeadTarget.email } : { id: '' }}
          dataName="lead"
          closeDeleteModal={() => setDeleteLeadTarget(null)}
          handleDelete={(leadId) => handleDeleteLead(leadId)}
        />

        <DeleteConfirmationModal
          isOpen={Boolean(deleteGroupTarget)}
          data={deleteGroupTarget ? { id: deleteGroupTarget.id, name: deleteGroupTarget.name } : { id: '' }}
          dataName="lead group"
          closeDeleteModal={() => setDeleteGroupTarget(null)}
          handleDelete={(groupId) => handleDeleteGroup(groupId)}
        />

        <LeadConfirmModal
          isOpen={Boolean(resendTarget)}
          title="Resend campaign?"
          message={campaignResendConfirmMessage(resendTarget ?? undefined)}
          confirmLabel="Resend"
          submitting={sendSubmitting}
          onClose={() => {
            if (sendSubmitting) return;
            setResendTarget(null);
          }}
          onConfirm={handleConfirmResendCampaign}
        />
      </div>
    </LeadsLayout>
  );
};

export default LeadsAdmin;
